/*
  Component: Accessibility widget (EqualWeb)
  Responsibility: lazy-load the EqualWeb accessibility script on demand, and keep its
                  floating button out of the way of this theme's full-screen overlays.

  The script is not loaded on every pageload — nothing calls out to EqualWeb until the
  footer trigger is clicked. Once loaded, the script exposes its own state on the page
  (an #INDshadowRootWrap element and a window.interdeal.a11y API); there is no bundler
  boundary to cross, so this file talks to that state directly rather than importing it.

  Overlay visibility is not wired into cart-drawer.js, kl-header.js, kl-search.js or
  kl-video-carousel-panel.js — three of those are Dawn vendor code, and per this repo's
  rule against reaching into vendor internals, this file instead watches the DOM signals
  those components already expose (open classes/attributes) with one MutationObserver,
  rather than editing five files to call out to a sixth.
*/

const KL_ACCESSIBILITY_SCRIPT_ID = 'equalweb-script';
const KL_ACCESSIBILITY_WIDGET_ID = 'INDshadowRootWrap';
const KL_ACCESSIBILITY_HIDDEN_CLASS = 'is-accessibility-widget-hidden';

const KL_ACCESSIBILITY_OPEN_OVERLAY_SELECTOR = [
  'cart-drawer.active',
  '[data-kl-header].is-menu-open',
  '[data-kl-search][open]',
  '[data-kl-video-carousel-panel-open]',
  'modal-dialog[open]',
].join(', ');

function getIsOverlayOpen() {
  return Boolean(document.querySelector(KL_ACCESSIBILITY_OPEN_OVERLAY_SELECTOR));
}

function updateWidgetVisibility() {
  const widget = document.getElementById(KL_ACCESSIBILITY_WIDGET_ID);

  if (!widget) return;

  widget.classList.toggle(KL_ACCESSIBILITY_HIDDEN_CLASS, getIsOverlayOpen());
}

/*
  One observer for both jobs, since both are "something under body changed": the widget
  arriving for the first time (a childList change, once EqualWeb's script finishes) and an
  overlay opening or closing (an attribute change on markup already in the DOM).
*/
function watchOverlayState() {
  updateWidgetVisibility();

  const observer = new MutationObserver(updateWidgetVisibility);

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class', 'open', 'data-kl-video-carousel-panel-open'],
    childList: true,
    subtree: true,
  });
}

function watchMenuReady() {
  try {
    if (window.interdeal?.a11y?.openMenu) {
      window.interdeal.a11y.openMenu();
      return;
    }
  } catch (error) {
    console.debug('kl-accessibility-widget: could not read the EqualWeb API yet', { error });
  }

  window.requestAnimationFrame(watchMenuReady);
}

function loadAccessibilityScript(sitekey) {
  if (document.getElementById(KL_ACCESSIBILITY_SCRIPT_ID)) {
    watchMenuReady();
    return;
  }

  window.interdeal = {
    sitekey,
    Position: 'left',
    Menulang: 'EN',
    domains: {
      js: 'https://cdn.equalweb.com/',
      acc: 'https://access.equalweb.com/',
    },
    btnStyle: {
      vPosition: ['calc(100% - 130px)', 'calc(100% - 130px)'],
      scale: ['0.5', '0.5'],
      color: {
        main: '#000000',
        second: '#ffffff',
      },
      icon: {
        outline: false,
        type: 11,
        shape: 'circle',
      },
    },
  };

  const script = document.createElement('script');
  script.id = KL_ACCESSIBILITY_SCRIPT_ID;
  script.src = 'https://cdn.equalweb.com/core/5.1.0/accessibility.js';
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.setAttribute('data-cfasync', 'true');
  script.addEventListener('error', () => {
    console.debug('kl-accessibility-widget: the EqualWeb script failed to load', { src: script.src });
  });

  document.body.appendChild(script);

  watchMenuReady();
}

function handleTriggerClick(event) {
  const trigger = event.target.closest('[data-kl-accessibility-trigger]');

  if (!trigger) return;

  event.preventDefault();

  const sitekey = trigger.getAttribute('data-kl-accessibility-trigger');

  if (!sitekey) return;

  try {
    loadAccessibilityScript(sitekey);
  } catch (error) {
    console.debug('kl-accessibility-widget: could not load the accessibility widget', { error });
  }
}

document.addEventListener('click', handleTriggerClick);

watchOverlayState();
